import { useEffect, useState } from 'react';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { Save, Settings as SettingsIcon } from 'lucide-react';
import { ERPUser } from '../../core/auth/types';
import { db } from '../../core/firebase/firebase';

type AppSettings = {
  companyName: string;
  logoUrl: string;
  phone: string;
  website: string;
  address: string;
  defaultCurrency: string;
  invoicePrefix: string;
  receiptPrefix: string;
  refundPrefix: string;
  voucherPrefix: string;
  whatsappFooter: string;
  emailFooter: string;
};

const defaults: AppSettings = {
  companyName: 'Sardar Patel Travel',
  logoUrl: 'https://sptravel-erp.web.app/assets/logo.png',
  phone: '+1 484-533-7033',
  website: 'www.sardarpateltravel.com',
  address: '2503 Clinton Dr., Galena Park, Houston, TX 77547',
  defaultCurrency: 'USD',
  invoicePrefix: 'INV',
  receiptPrefix: 'RCT',
  refundPrefix: 'RF',
  voucherPrefix: 'VCH',
  whatsappFooter: 'Thank you, Sardar Patel Travel',
  emailFooter: 'Regards,\\nSardar Patel Travel'
};

export default function SettingsPage({ user }: { user: ERPUser }) {
  const [form, setForm] = useState<AppSettings>(defaults);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const canEdit = user.role === 'super_admin' || user.role === 'admin';

  async function loadSettings() {
    const snap = await getDoc(doc(db, 'settings', 'company'));
    if (snap.exists()) setForm({ ...defaults, ...(snap.data() as Partial<AppSettings>) });
  }

  useEffect(() => {
    loadSettings().catch((error) => setMessage(error instanceof Error ? error.message : 'Unable to load settings.'));
  }, []);

  async function saveSettings() {
    if (!canEdit) {
      setMessage('Only admin/super admin can update settings.');
      return;
    }
    setBusy(true);
    setMessage('');
    try {
      await setDoc(doc(db, 'settings', 'company'), { ...form, updatedAt: serverTimestamp(), updatedBy: user.uid }, { merge: true });
      setMessage('Settings saved successfully.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to save settings.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <p className="eyebrow">System Setup</p>
          <h1>Settings</h1>
          <p>Company branding, document prefixes, default currency, logo URL, and communication footer settings.</p>
        </div>
        <button className="btn primary" title="Save settings" disabled={busy || !canEdit} onClick={saveSettings}>
          <Save size={18} /> {busy ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {message && <div className="error-box" style={{ marginBottom: 20 }}>{message}</div>}

      <section className="card">
        <h3><SettingsIcon size={18} /> Company Profile</h3>
        <div className="form-grid">
          <label>Company Name<input value={form.companyName} disabled={!canEdit} onChange={(event) => setForm({ ...form, companyName: event.target.value })} /></label>
          <label>Logo URL<input value={form.logoUrl} disabled={!canEdit} onChange={(event) => setForm({ ...form, logoUrl: event.target.value })} /></label>
          <label>Phone<input value={form.phone} disabled={!canEdit} onChange={(event) => setForm({ ...form, phone: event.target.value })} /></label>
          <label>Website<input value={form.website} disabled={!canEdit} onChange={(event) => setForm({ ...form, website: event.target.value })} /></label>
          <label>Default Currency<select value={form.defaultCurrency} disabled={!canEdit} onChange={(event) => setForm({ ...form, defaultCurrency: event.target.value })}><option>USD</option><option>INR</option><option>CAD</option></select></label>
          <label className="span-3">Address<input value={form.address} disabled={!canEdit} onChange={(event) => setForm({ ...form, address: event.target.value })} /></label>
        </div>
      </section>

      <section className="card">
        <h3>Document Prefixes</h3>
        <div className="form-grid">
          <label>Invoice Prefix<input value={form.invoicePrefix} disabled={!canEdit} onChange={(event) => setForm({ ...form, invoicePrefix: event.target.value })} /></label>
          <label>Receipt Prefix<input value={form.receiptPrefix} disabled={!canEdit} onChange={(event) => setForm({ ...form, receiptPrefix: event.target.value })} /></label>
          <label>Refund Prefix<input value={form.refundPrefix} disabled={!canEdit} onChange={(event) => setForm({ ...form, refundPrefix: event.target.value })} /></label>
          <label>Voucher Prefix<input value={form.voucherPrefix} disabled={!canEdit} onChange={(event) => setForm({ ...form, voucherPrefix: event.target.value })} /></label>
        </div>
      </section>

      <section className="card">
        <h3>Communication Footer</h3>
        <div className="form-grid">
          <label className="span-3">WhatsApp Footer<textarea value={form.whatsappFooter} disabled={!canEdit} onChange={(event) => setForm({ ...form, whatsappFooter: event.target.value })} /></label>
          <label className="span-3">Email Footer<textarea value={form.emailFooter} disabled={!canEdit} onChange={(event) => setForm({ ...form, emailFooter: event.target.value })} /></label>
        </div>
      </section>
    </div>
  );
}
